<template>
  <div
    :class="{
      'advance-reporting-chart-card': true,
      'chart-card-border': !inModalView
    }"
    :style="gridStyles"
  >
    <!-- Chart Header -->
    <div class="chart-header">
      <!-- Chart Left Side -->
      <div class="chart-title-container">
        <h3 class="chart-title">{{ chart?.title }}</h3>
        <VTooltip>
          <template #popper>
            <div>
              {{ chart.information }}
            </div>
          </template>
          <div v-if="chart.information" class="chart-info">
            <i class="ri-information-line"></i>
          </div>
        </VTooltip>
      </div>
      <!-- Chart Right Side -->
      <div>
        <template v-if="chartSlots.showComparisonLegends">
          <div class="chart-comparison-legends">
            <div
              v-for="item in comparisonLegendOptions"
              :key="item.label"
              class="legend-item"
            >
              <div
                class="legend-color"
                :style="{ backgroundColor: item.color }"
              ></div>
              <span>{{ item.label }}</span>
            </div>
          </div>
        </template>

        <template v-if="chartSlots.showChartTableToggle">
          <button
            type="button"
            class="table-view-btn"
            :aria-pressed="state.isTableView"
            @click="toggleTableView"
          >
            <template v-if="!state.isTableView">
              Table View <i class="ri-table-line" aria-hidden="true"></i>
            </template>
            <template v-else>
              Chart View <i class="ri-bar-chart-line" aria-hidden="true"></i>
            </template>
          </button>
        </template>

        <template v-if="chartSlots.showRevealNameToggle">
          <button
            type="button"
            class="reveal-names-btn"
            :aria-pressed="state.areNamesRevealed"
            @click="toggleRevealNames"
          >
            <template v-if="state.areNamesRevealed">
              Hide Names <i class="ri-eye-off-line" aria-hidden="true"></i>
            </template>
            <template v-else>
              Reveal Names <i class="ri-eye-line" aria-hidden="true"></i>
            </template>
          </button>
        </template>
      </div>
    </div>

    <div v-if="showContentBody" class="content-body">
      <!-- Table Content Body -->
      <template v-if="state.isTableView">
        <div
          class="table-view"
          :class="{
            'table-view--scroll': isTotalPassesBySchoolsTable
          }"
          :style="totalPassesBySchoolsTableScrollStyle"
        >
          <table class="data-table">
            <thead>
              <tr>
                <th v-for="header in tableHeaders" :key="header">
                  {{ header }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in tableData" :key="index">
                <td v-for="header in tableHeaders" :key="header">
                  {{ item[header] }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- Chart Content Body -->
      <HighChart v-else :options="chartOptions" class="content-body-chart" />
    </div>
    <div v-else class="content-body align-items-center justify-content-center">
      <template v-if="showChartNoDataMessage">
        <div class="chart-error-message">
          <i class="ri ri-alert-line"></i>
          We could not find the chart data for this chart.
        </div>
      </template>
    </div>

    <!-- Chart Footer -->
    <div>
      <template v-if="chartSlots.showFooterLegends">
        <div class="chart-legend">
          <div
            v-for="item in footerLegendOptions"
            :key="item.label"
            class="legend-item"
          >
            <div
              class="legend-color"
              :style="{ backgroundColor: item.color }"
            ></div>
            <span>{{ item.label }}</span>
          </div>
        </div>
      </template>

      <template v-if="chartSlots.showViewMoreToggle">
        <div class="view-more-container">
          <button
            type="button"
            class="view-more-btn"
            aria-haspopup="dialog"
            @click="openViewMorePopUp"
          >
            View More
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { useGradeYears } from "@/composables/useGradeYears.js"
import advanceReportingConstants from "@/constants/advanceReportingConstants"
import { escapeHtml, escapeHtmlList } from "@/helpers/escapeHtml"
import Highcharts from "highcharts"
import { Chart } from "highcharts-vue"
import moment from "moment-timezone"
import {
  computed,
  defineAsyncComponent,
  inject,
  onMounted,
  onUnmounted,
  reactive
} from "vue"
import { useStore } from "vuex"

const ChartViewMorePopUp = defineAsyncComponent(
  () => import("@/v3components/AdvanceReporting/Chart.vue")
)

// Advance Reporting is light-only. Highcharts 13 defaults to colorScheme
// "light dark", which follows OS dark mode and darkens pie borders / grids.
const LIGHT_CHART_PALETTE = Object.freeze({
  colorScheme: "light"
})

Highcharts.setOptions({
  palette: LIGHT_CHART_PALETTE
})

const CHART_COLOR_MAP = {
  BLUE: "#338FEB",
  PURPLE: "#8783F7",
  TEAL: "#379DAB",
  PINK: "#D369D0",
  ORANGE: "#C87012",
  GREEN: "#008566",
  YELLOW: "#F6C026",
  LIME: "#82B92C"
}

const COLOR_LIST = [
  CHART_COLOR_MAP.BLUE,
  CHART_COLOR_MAP.PURPLE,
  CHART_COLOR_MAP.TEAL,
  CHART_COLOR_MAP.PINK,
  CHART_COLOR_MAP.ORANGE,
  CHART_COLOR_MAP.GREEN,
  CHART_COLOR_MAP.YELLOW,
  CHART_COLOR_MAP.LIME
]

const chartUiConfigs = {
  [advanceReportingConstants.DEFAULT_CHART_IDS.SCHOOL_YEAR_PASS_TREND]: {
    yAxisTitle: "Passes"
  },
  [advanceReportingConstants.DEFAULT_CHART_IDS.PASS_TREND]: {
    yAxisTitle: "Passes"
  },
  [advanceReportingConstants.DEFAULT_CHART_IDS.TOTAL_PASSES_BY_SCHOOLS]: {
    yAxisTitle: "Visit Frequency",
    chartTopLimit: 10,
    tableScrollRows: 10,
    tableHeaderColumns: {
      name: "Name",
      student: "Student",
      kiosk: "Kiosk",
      teacher: "Teacher",
      appointment: "Appointment",
      slp: "SLP"
    }
  },
  [advanceReportingConstants.DEFAULT_CHART_IDS.MOST_PASSES_TAKEN]: {
    yAxisTitle: "Passes"
  },
  [advanceReportingConstants.DEFAULT_CHART_IDS.MOST_PASSES_GRANTED]: {
    yAxisTitle: "Passes"
  },
  [advanceReportingConstants.DEFAULT_CHART_IDS.MOST_PASSES_TAKEN]: {
    yAxisTitle: "Number of passes",
    dataType: {
      user: "Student"
    }
  },
  [advanceReportingConstants.DEFAULT_CHART_IDS.MOST_PASSES_GRANTED]: {
    yAxisTitle: "Number of passes",
    dataType: {
      user: "Adult"
    }
  },
  [advanceReportingConstants.DEFAULT_CHART_IDS.PASS_BREAKDOWN_BY_GRAD_YEAR]: {
    yAxisTitle: "Passes"
  },
  [advanceReportingConstants.DEFAULT_CHART_IDS.AVERAGE_PASS_DURATION]: {
    yAxisTitle: "Average Passes Duration (minutes)"
  },
  [advanceReportingConstants.DEFAULT_CHART_IDS.MOST_FREQUENTED_LOCATIONS]: {
    xAxisTitle: "Number of Visits"
  },
  [advanceReportingConstants.DEFAULT_CHART_IDS.TARDY_INFRACTIONS]: {
    yAxisTitle: "Tardy Infractions (SLP+LTC+LTS)"
  },
  [advanceReportingConstants.DEFAULT_CHART_IDS.CONTACT_CONTROL]: {
    xAxisTitle: "Contact Preventions",
    dataType: {
      user: "Student Pair"
    }
  },
  [advanceReportingConstants.DEFAULT_CHART_IDS.VISITORS_TREND]: {
    yAxisTitle: "Visitors"
  },
  [advanceReportingConstants.DEFAULT_CHART_IDS.ZERO_GRANTERS]: {
    yAxisTitle: "Users with 0 passes granted"
  }
}

export default {
  name: "Chart",
  components: {
    HighChart: Chart
  },
  props: {
    chart: {
      type: Object,
      default: null
    },
    isDateDependent: {
      type: Boolean,
      default: false
    },
    inModalView: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const store = useStore()
    const modal = inject("modal")
    const { currentSchoolYear } = useGradeYears()

    const state = reactive({
      isTableView: false,
      areNamesRevealed: false
    })

    const isDateDependentChartDataLoading = computed(() => {
      return (
        store?.state?.advanceReportingStore?.isDateDependentChartDataLoading ||
        false
      )
    })

    const showChartNoDataMessage = computed(() => {
      if (props?.isDateDependent) {
        if (isDateDependentChartDataLoading?.value) {
          return false
        }
        return !showContentBody?.value
      }
      return !showContentBody?.value
    })

    const gridStyles = computed(() => {
      if (!props?.chart?.colSpan) return {}
      return {
        gridColumn: `span ${props?.chart?.colSpan}`
      }
    })

    const heightOfChart = computed(() => {
      return props?.inModalView ? 600 : 300
    })

    const dateIndependentChartData = computed(() => {
      return (
        store?.getters?.["advanceReportingStore/dateIndependentChartData"] ||
        null
      )
    })

    const dateDependentChartData = computed(() => {
      return (
        store?.getters?.["advanceReportingStore/dateDependentChartData"] || null
      )
    })

    const selectedDateRange = computed(() => {
      return store?.getters?.["advanceReportingStore/selectedDateRange"] || null
    })

    const selectedSchoolYear = computed(() => {
      return (
        store?.getters?.["advanceReportingStore/selectedSchoolYear"] || null
      )
    })

    const isHistoricalSchoolYear = computed(() => {
      const selectedLabel = selectedSchoolYear.value?.label || null
      const currentLabel = currentSchoolYear.value?.label || null

      return Boolean(
        selectedLabel && currentLabel && selectedLabel !== currentLabel
      )
    })

    const revealedNamesMatrix = computed(() => {
      return store?.getters?.["advanceReportingStore/revealedNamesMatrix"] || {}
    })

    const chartData = computed(() => {
      return props?.isDateDependent
        ? dateDependentChartData?.value?.[props?.chart?.id] || null
        : dateIndependentChartData?.value?.[props?.chart?.id] || null
    })

    const chartConfig = computed(() => {
      return chartUiConfigs[props?.chart?.id] || null
    })

    const chartSlots = computed(() => {
      const showComparisonLegends =
        props?.chart?.slots?.header?.comparisonLegends &&
        props?.chart?.type === "line"
      const showChartTableToggle =
        props?.chart?.slots?.header?.chartTableToggle &&
        props?.chart?.type === "column"
      const showRevealNameToggle =
        props?.chart?.slots?.header?.revealNameToggle &&
        props?.chart?.type === "column"
      const showViewMoreToggle =
        props?.chart?.slots?.footer?.viewMoreToggle &&
        props?.chart?.type === "column" &&
        !props?.inModalView
      const showFooterLegends =
        props?.chart?.slots?.footer?.legends && props?.chart?.type === "pie"
      return {
        showComparisonLegends,
        showChartTableToggle,
        showRevealNameToggle,
        showViewMoreToggle,
        showFooterLegends
      }
    })

    const tableHeaders = computed(() => {
      const data = chartData?.value?.detailed?.[0] || null
      if (!data) return []
      return (
        Object?.keys(data)?.map(
          (item) => chartConfig?.value?.tableHeaderColumns?.[item] || item
        ) || []
      )
    })

    const tableData = computed(() => {
      const data = chartData?.value?.detailed || []
      if (!data?.length || !chartConfig?.value?.tableHeaderColumns) return []

      return data.map((item) => {
        const transformedItem = {}
        Object.keys(item).forEach((key) => {
          const displayKey =
            chartConfig?.value?.tableHeaderColumns?.[key] || key
          transformedItem[displayKey] = item[key]
        })
        return transformedItem
      })
    })

    const isTotalPassesBySchoolsTable = computed(() => {
      return (
        state.isTableView &&
        props?.chart?.id ===
          advanceReportingConstants.DEFAULT_CHART_IDS.TOTAL_PASSES_BY_SCHOOLS
      )
    })

    const TABLE_ROW_HEIGHT_PX = 40
    const totalPassesBySchoolsTableScrollStyle = computed(() => {
      if (!isTotalPassesBySchoolsTable?.value) return {}
      const scrollRows = chartConfig?.value?.tableScrollRows ?? 15
      return {
        maxHeight: `${scrollRows * TABLE_ROW_HEIGHT_PX}px`,
        overflowY: "auto"
      }
    })

    const showContentBody = computed(() => {
      if (chartSlots?.value?.showChartTableToggle) {
        return tableHeaders?.value?.length > 0 && chartOptions?.value !== null
      }
      return chartOptions?.value !== null
    })

    const filteredChartData = computed(() => {
      const data = chartData?.value || null
      if (!data) return null

      const config = chartConfig?.value || null
      const chartTopLimit = config?.chartTopLimit
      const limit =
        chartTopLimit != null ? chartTopLimit : props?.inModalView ? 20 : 5
      const hasViewMoreToggle = chartSlots?.value?.showViewMoreToggle
      const applyLimit = hasViewMoreToggle || chartTopLimit != null

      if (!applyLimit) {
        return data
      }

      return {
        ...data,
        categories: data?.categories?.slice(0, limit) || [],
        values: data?.values?.slice(0, limit) || []
      }
    })

    const displayCategories = computed(() => {
      const data = filteredChartData?.value || null
      const config = chartConfig?.value || null
      const originalData = chartData?.value || null

      if (!data?.categories?.length) return []

      const schools = originalData?.schools || null
      const categoriesLength = data?.categories?.length || 0
      const schoolsSlice = schools ? schools.slice(0, categoriesLength) : null

      let baseCategories = data?.categories || []

      if (schoolsSlice && Array.isArray(schoolsSlice)) {
        baseCategories = baseCategories.map((category, index) => {
          const schoolName = String(schoolsSlice?.[index] || "").trim() || ""
          if (schoolName) {
            return `${String(category)} - ${schoolName}`
          }
          return String(category)
        })
      }

      if (state.areNamesRevealed || !config?.dataType?.user) {
        return escapeHtmlList(baseCategories)
      }

      const userType = config?.dataType?.user
      return baseCategories.map((_, index) => `${userType} ${index + 1}`)
    })

    const createPieChartOptions = () => {
      const data = chartData?.value
      if (!data) return null
      const options = {
        chart: {
          type: "pie",
          height: heightOfChart?.value,
          backgroundColor: "transparent"
        },
        palette: LIGHT_CHART_PALETTE,
        accessibility: { enabled: false },
        title: { text: null },
        plotOptions: {
          pie: { innerSize: "60%", dataLabels: { enabled: false } }
        },
        series: [
          {
            data: data?.map((item, index) => ({
              name: escapeHtml(item?.name),
              y: item?.value,
              color: COLOR_LIST[index]
            }))
          }
        ],
        credits: { enabled: false },
        legend: { enabled: false }
      }

      return options
    }

    const createLineChartOptions = () => {
      const data = chartData?.value || null
      const config = chartConfig?.value || null
      if (!data || !config) {
        return null
      }

      // Check if it's custom range - only show current data
      const isCustomRange =
        props?.isDateDependent &&
        selectedDateRange?.value?.type ===
          advanceReportingConstants.DATE_RANGE_TYPES.CUSTOM

      const options = {
        chart: {
          type: "line",
          height: heightOfChart?.value,
          backgroundColor: "transparent"
        },
        palette: LIGHT_CHART_PALETTE,
        accessibility: { enabled: false },
        title: { text: null },
        xAxis: {
          categories: escapeHtmlList(data?.categories || []),
          ...(config?.xAxisTitle && {
            title: {
              text: config?.xAxisTitle || ""
            }
          })
        },
        yAxis: {
          ...(config?.yAxisTitle && {
            title: {
              text: config?.yAxisTitle || ""
            }
          })
        },
        series: isCustomRange
          ? [
              {
                data: data?.current || [],
                color: COLOR_LIST[0]
              }
            ]
          : ["current", "previous"].map((item, index) => ({
              data: data?.[item] || [],
              color: COLOR_LIST[index]
            })),
        credits: { enabled: false },
        legend: { enabled: false }
      }
      return options
    }

    const createColumnChartOptions = () => {
      const data = filteredChartData?.value || null
      const config = chartConfig?.value || null
      if (!data || !config) {
        return null
      }
      if (props?.chart?.inverted) {
        const options = {
          chart: {
            type: "bar",
            height: heightOfChart?.value,
            backgroundColor: "transparent"
          },
          palette: LIGHT_CHART_PALETTE,
          accessibility: { enabled: false },
          title: { text: null },
          xAxis: {
            categories: displayCategories?.value || [],
            ...(config?.yAxisTitle && {
              title: {
                text: config?.yAxisTitle || ""
              }
            })
          },
          yAxis: {
            ...(config?.xAxisTitle && {
              title: {
                text: config?.xAxisTitle || ""
              }
            })
          },
          series: [
            {
              data: data?.values || [],
              color: CHART_COLOR_MAP.BLUE
            }
          ],
          credits: { enabled: false },
          legend: { enabled: false },
          detailed: data?.detailed || []
        }
        return options
      } else {
        const options = {
          chart: {
            type: "column",
            height: heightOfChart?.value,
            backgroundColor: "transparent"
          },
          palette: LIGHT_CHART_PALETTE,
          title: { text: null },
          accessibility: { enabled: false },
          xAxis: {
            categories: displayCategories?.value || [],
            ...(config?.xAxisTitle && {
              title: {
                text: config?.xAxisTitle || ""
              }
            })
          },
          yAxis: {
            ...(config?.yAxisTitle && {
              title: {
                text: config?.yAxisTitle || ""
              }
            })
          },
          series: [
            {
              data: data?.values || [],
              color: CHART_COLOR_MAP.BLUE
            }
          ],
          credits: { enabled: false },
          legend: { enabled: false },
          detailed: data?.detailed || []
        }
        return options
      }
    }

    const chartOptions = computed(() => {
      switch (props?.chart?.type) {
        case "pie":
          return createPieChartOptions()
        case "line":
          return createLineChartOptions()
        case "column":
          return createColumnChartOptions()
      }
      return null
    })

    const hasChartOptions = computed(() => {
      return chartOptions?.value !== null
    })

    const comparisonLegendOptions = computed(() => {
      if (!hasChartOptions?.value) return []
      if (
        props?.chart?.id ===
        advanceReportingConstants.DEFAULT_CHART_IDS.SCHOOL_YEAR_PASS_TREND
      ) {
        const currentDate = moment()
        const currentYear = currentDate.year()
        const currentMonthIndex = currentDate.month() // 0-based: 0=Jan, 6=Jul
        const schoolYearStartYear =
          currentMonthIndex >= 6 ? currentYear : currentYear - 1
        const thisYearLabel = `This year (Jul ${schoolYearStartYear} - Jun ${schoolYearStartYear + 1})`
        const lastYearLabel = `Last year (Jul ${schoolYearStartYear - 1} - Jun ${schoolYearStartYear})`
        return [thisYearLabel, lastYearLabel].map((item, index) => ({
          label: item,
          color: COLOR_LIST[index]
        }))
      }

      // For date-dependent charts, use dynamic labels based on selected date range
      if (props?.isDateDependent && selectedDateRange?.value?.type) {
        const dateRangeType = selectedDateRange?.value?.type
        const startDate = selectedDateRange?.value?.startDate || null
        const endDate = selectedDateRange?.value?.endDate || null
        const previousStartDate =
          selectedDateRange?.value?.previousStartDate || null
        const previousEndDate =
          selectedDateRange?.value?.previousEndDate || null

        if (
          dateRangeType === advanceReportingConstants.DATE_RANGE_TYPES.CUSTOM
        ) {
          return []
        }

        if (!startDate || !endDate || !previousStartDate || !previousEndDate) {
          return []
        }

        const currentStart = moment(startDate, "YYYY-MM-DD")
        const currentEnd = moment(endDate, "YYYY-MM-DD")
        const previousStart = moment(previousStartDate, "YYYY-MM-DD")
        const previousEnd = moment(previousEndDate, "YYYY-MM-DD")
        const useDateOnlyLabels = isHistoricalSchoolYear.value

        let currentLabel = ""
        let previousLabel = ""

        switch (dateRangeType) {
          case advanceReportingConstants.DATE_RANGE_TYPES.YESTERDAY: {
            currentLabel = useDateOnlyLabels
              ? currentEnd.format("MMM D, YYYY")
              : `Previous day (${currentEnd.format("MMM D, YYYY")})`
            previousLabel = useDateOnlyLabels
              ? previousEnd.format("MMM D, YYYY")
              : `2 days ago (${previousEnd.format("MMM D, YYYY")})`
            break
          }
          case advanceReportingConstants.DATE_RANGE_TYPES.THIS_WEEK: {
            currentLabel = useDateOnlyLabels
              ? `${currentStart.format("MMM D, YYYY")} - ${currentEnd.format("MMM D, YYYY")}`
              : `This week (${currentStart.format("MMM D, YYYY")} - ${currentEnd.format("MMM D, YYYY")})`
            previousLabel = useDateOnlyLabels
              ? `${previousStart.format("MMM D, YYYY")} - ${previousEnd.format("MMM D, YYYY")}`
              : `Last week (${previousStart.format("MMM D, YYYY")} - ${previousEnd.format("MMM D, YYYY")})`
            break
          }
          case advanceReportingConstants.DATE_RANGE_TYPES.THIS_MONTH: {
            currentLabel = useDateOnlyLabels
              ? `${currentStart.format("MMM D, YYYY")} - ${currentEnd.format("MMM D, YYYY")}`
              : `This month (${currentStart.format("MMM D, YYYY")} - ${currentEnd.format("MMM D, YYYY")})`
            previousLabel = useDateOnlyLabels
              ? `${previousStart.format("MMM D, YYYY")} - ${previousEnd.format("MMM D, YYYY")}`
              : `Last month (${previousStart.format("MMM D, YYYY")} - ${previousEnd.format("MMM D, YYYY")})`
            break
          }
          case advanceReportingConstants.DATE_RANGE_TYPES.THIS_YEAR: {
            currentLabel = useDateOnlyLabels
              ? `${currentStart.format("MMM YYYY")} - ${currentEnd.format("MMM YYYY")}`
              : `This year (${currentStart.format("MMM YYYY")} - ${currentEnd.format("MMM YYYY")})`
            previousLabel = useDateOnlyLabels
              ? `${previousStart.format("MMM YYYY")} - ${previousEnd.format("MMM YYYY")}`
              : `Last year (${previousStart.format("MMM YYYY")} - ${previousEnd.format("MMM YYYY")})`
            break
          }
          default: {
            return []
          }
        }

        return [currentLabel, previousLabel].map((item, index) => ({
          label: item,
          color: COLOR_LIST[index]
        }))
      }

      return []
    })

    const footerLegendOptions = computed(() => {
      const data = chartData?.value
      return data?.map((item, index) => ({
        label: item?.name,
        color: COLOR_LIST[index]
      }))
    })

    const toggleTableView = () => {
      state.isTableView = !state.isTableView
    }

    const openViewMorePopUp = () => {
      return modal.open(ChartViewMorePopUp, {
        size: "lg",
        title: props?.chart?.title,
        props: {
          chart: props?.chart,
          isDateDependent: props?.isDateDependent,
          inModalView: true
        }
      })
    }

    const toggleRevealNames = () => {
      state.areNamesRevealed = !state.areNamesRevealed
      const clonedRevealedNamesMatrix = {
        ...(revealedNamesMatrix?.value || {})
      }
      clonedRevealedNamesMatrix[props?.chart?.id] = state.areNamesRevealed
      store.commit(
        "advanceReportingStore/setRevealedNamesMatrix",
        clonedRevealedNamesMatrix
      )
    }

    onMounted(() => {
      state.areNamesRevealed =
        revealedNamesMatrix?.value?.[props?.chart?.id] || false
    })

    onUnmounted(() => {
      if (Object.keys(revealedNamesMatrix?.value || {}).length > 0) {
        store.commit("advanceReportingStore/setRevealedNamesMatrix", {})
      }
    })

    return {
      state,
      gridStyles,
      chartOptions,
      hasChartOptions,
      chartSlots,
      tableHeaders,
      tableData,
      isTotalPassesBySchoolsTable,
      totalPassesBySchoolsTableScrollStyle,
      footerLegendOptions,
      showContentBody,
      comparisonLegendOptions,
      showChartNoDataMessage,
      toggleTableView,
      openViewMorePopUp,
      toggleRevealNames
    }
  }
}
</script>

<style scoped>
.table-view--scroll .data-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--cui-tertiary-bg, #e9ecef);
  box-shadow: 0 1px 0 0 var(--cui-border-color, rgba(0, 0, 0, 0.08));
}
</style>
